import type { ThunkExtras, ThunkAction, GetState, APIError, AppState, Dispatch } from '../../types/app.types';
import type {
  NimbusState,
  NimbusCreate,
  NimbusAction,
  NimbusPurchaseResponse,
  NimbusResponse,
} from './nimbus-module.types';
import { updateAccountState, getAccountSummary } from './account-module';
import { getAccountOrders } from './orders-module';
import { trackEvent } from '../../utils/analytics';

export const createNimbusAccount =
  (data: NimbusCreate): ThunkAction<Promise<any>> =>
  (dispatch: Dispatch, getState: GetState, extras: ThunkExtras): Promise<any> => {
    return extras.blinkAPIClient
      .post('/v2/user/account/signup/nimbus', data)
      .then((res) => res.data)
      .then((data) => data.result)
      .then((data) => dispatch(updateAccountState(data)))
      .then(() => {
        return dispatch(getAccountSummary());
      })
      .catch((err: APIError) => {
        return dispatch({
          type: 'nimbus/NIMBUS_ERROR',
          ...err,
        });
      });
  };
export const nimbusPurchase =
  (): ThunkAction<Promise<any>> =>
  (dispatch: Dispatch, getState: GetState, extras: ThunkExtras): Promise<any> => {
    const appState = getState();
    return extras.blinkAPIClient
      .post('/v2/nimbus_cart/purchase')
      .then((res) => res.data)
      .then((data) => data.result)
      .then((response: NimbusPurchaseResponse) => {
        dispatch(getAccountSummary());
        dispatch(
          trackEvent('Completed Order', {
            orderCount: 1,
            orderId: response.orderId,
            pharmacyId: response.pharmacyId,
            sessionId: appState.account.sessionId,
            pharmacyNetworkId: response.pharmacyNetworkId,
            cartFlowId: response.cartFlowId,
            isSubs: false,
            // Nimbus is only for travatan z
            isTelemed: false, // Nimbus is only for travatan z
          })
        );
        dispatch(getAccountOrders());
        dispatch({
          type: 'nimbus/GET_NIMBUS_CART',
          response,
        });
      })
      .then(() => {
        return dispatch(navigateToNimbusCheckoutStep());
      })
      .catch((err) => {
        dispatch({
          type: 'cart/PURCHASE_ERROR',
          ...err,
        });
        throw err;
      });
  };

export function getCloudPharmaPatient(token?: string | null | undefined): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, extras: ThunkExtras) {
    return extras.blinkAPIClient
      .get('/v2/user/account/signup/nimbus', {
        params: {
          exit_token: token || null,
        },
      })
      .then((res) => res.data)
      .then((data) => data.result);
  };
}

export const getCloudPharmaPatientInfo =
  (token?: string | null | undefined): ThunkAction<Promise<any>> =>
  (dispatch: Dispatch) => {
    return dispatch(getCloudPharmaPatient(token))
      .then((data: NimbusResponse) => {
        return dispatch({
          type: 'nimbus/GET_NIMBUS_STATE',
          data,
          token,
        });
      })
      .catch((err: APIError) => {
        return dispatch({
          type: 'nimbus/NIMBUS_ERROR',
          ...err,
        });
      });
  };

export function navigateToNimbusCheckoutStep(replace?: boolean): ThunkAction {
  return function (dispatch: Dispatch, getState: GetState, { history }: ThunkExtras): void {
    const route = getNimbusRouteFromState(getState());
    return replace ? history.replace(route) : history.push(route);
  };
}

export function nimbusLogOut(): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, extras: ThunkExtras): Promise<any> {
    return extras.blinkAPIClient
      .post('/v2/user/account/logout') // if logout was successful we just reload to clear the store
      .then(() => document.dispatchEvent(new Event('TEARDOWN')))
      .catch((err: APIError) => {
        return dispatch({
          type: 'account/LOG_OUT_ERROR',
          ...err,
        });
      });
  };
}

function getNimbusRouteFromState(state: AppState): string {
  const { account, session, paymentMethods, nimbus } = state;
  const orderId = nimbus.orderId;
  const isLoggedIn = session.authenticated;
  const hasShippingAddress = account.hasShippingAddress;
  const hasCards = account.paymentCardCount > 0 || (paymentMethods && paymentMethods.length > 0);
  if (isLoggedIn && hasShippingAddress && hasCards && orderId) return `/specialty/8/${orderId}`;
  if (isLoggedIn && hasShippingAddress && hasCards) return '/specialty/7';
  if (isLoggedIn && hasShippingAddress) return '/specialty/6';
  if (isLoggedIn) return '/specialty/4';
  return '/specialty/1';
}

const initialState: NimbusState = {
  firstName: null,
  lastName: null,
  gender: null,
  birthDate: null,
  cellPhone: null,
  insurance: null,
  hubTasks: [],
  orderId: null,
};

export function convertCentsToDollars(number: number | null | undefined, decimals: number) {
  if (number) {
    const dollars = number / 100;
    return dollars.toFixed(decimals);
  }
}

const NimbusReducer = (state: NimbusState = { ...initialState }, action: NimbusAction): NimbusState => {
  switch (action.type) {
    case 'nimbus/GET_NIMBUS_STATE':
      return { ...state, ...action.data };

    case 'nimbus/GET_NIMBUS_CART':
      return { ...state, orderId: action.response.orderId };

    default:
      return state;
  }
};

export default NimbusReducer;
